import { useLang } from '../i18n'
import { useConnected, type Connected } from '../lib/connected'
import { colorFromName, initials, RobotShape } from './AgentAvatar'

/** Avatars shown before the row collapses into a "+N" chip. */
const MAX_AVATARS = 5

/**
 * The connected/editing-user roster — one rounded-initials avatar per member of the open bid,
 * a robot silhouette standing in for one whose console agent is working. Lives in the breadcrumb
 * bar, at the right of the "N connected" pill, and built from the SAME list (lib/connected.ts): the
 * pill counts exactly the people shown here (avatars + "+N"), bid-wide, whether a part is open or
 * not. Not in the top bar, which carries the product identity and the signed-in member's own
 * account menu instead.
 */
export function ConnectedRoster({ bid }: { bid: string | null }) {
  const { t } = useLang()
  const roster = useConnected(bid)

  const shown = roster.slice(0, MAX_AVATARS)
  const hidden = roster.length - shown.length
  // The busy state has no visual-only encoding: it is part of every avatar's accessible name.
  const label = (u: Connected): string =>
    `${u.name}${u.me ? ` (${t('connected_you')})` : ''}${u.busy ? ` — ${t('connected_busy')}` : ''}`

  return (
    <div className="avatars" role="list" aria-label={t('connected')}>
      {shown.map((u) => (
        <div
          key={u.id}
          role="listitem"
          className={u.busy ? 'av bot' : 'av'}
          style={u.busy ? { color: colorFromName(u.name) } : { background: colorFromName(u.name) }}
          title={label(u)}
          aria-label={label(u)}
        >
          {u.busy && <RobotShape />}
          <span>{initials(u.name)}</span>
        </div>
      ))}
      {hidden > 0 && (
        <div
          role="listitem"
          className="av more"
          title={t('connected_more').replace('{n}', () => String(hidden))}
          aria-label={t('connected_more').replace('{n}', () => String(hidden))}
        >
          <span>+{hidden}</span>
        </div>
      )}
    </div>
  )
}
